import { useMemo } from 'react';
import GlobalStyles from '@mui/material/GlobalStyles';
import { useTheme, css } from '@mui/material/styles';
import { useAppSelector } from 'hooks';
import { selectFont } from 'reducers/config';

const GlobalStyle = () => {
  const theme = useTheme();
  const font = useAppSelector(selectFont);

  const styles = useMemo(() => {
    return css`
      :root {
        // This color is not in our palette.
        // Needed to meet accessibility criterion.
        --message-link-color: #0064fa;

        --font-fallback:
          -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
          'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
          sans-serif;
      }

      body {
        color: ${theme.palette.text.primary};
        font-family: ${font}, var(--font-fallback) !important;
        font-size: 16px;
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
        font-weight: 400;
        line-height: 22px;
        overflow: hidden;
        text-rendering: optimizeLegibility;
      }

      *,
      ::before,
      ::after {
        font-family: inherit !important;
      }

      button,
      input,
      optgroup,
      select,
      textarea {
        font-size: inherit;
        line-height: inherit;
      }

      // Taken from https://github.com/sindresorhus/modern-normalize
      code,
      kbd,
      samp,
      pre {
        font-family:
          ui-monospace, SFMono-Regular, Consolas, 'Liberation Mono', Menlo,
          monospace !important; /* 1 */
        font-size: 1em; /* 2 */
      }

      pre,
      code:not(pre code) {
        background-color: ${theme.palette.colors.slate[200]};
        border: 1px solid ${theme.palette.colors.slate[400]};
        border-radius: 4px;
      }

      pre {
        padding: 8px;
        overflow-x: auto;
      }

      code:not(pre code) {
        padding: 2px 4px;
      }

      // Less padding for the top lists:
      ul,
      ol {
        padding-left: 10px;
      }

      // Default padding for nested lists:
      ul ul,
      ol ol,
      ul ol,
      ol ul {
        padding-left: 40px;
      }

      .MuiInputBase-input {
        font-size: 16px !important;
      }

      @media print {
        body {
          display: none;
        }
      }

      // Taken from https://github.com/twbs/bootstrap/blob/main/scss/mixins/_visually-hidden.scss
      .visually-hidden {
        width: 1px !important;
        height: 1px !important;
        padding: 0 !important;
        margin: -1px !important; // Fix for https://github.com/twbs/bootstrap/issues/25686
        overflow: hidden !important;
        clip: rect(0, 0, 0, 0) !important;
        white-space: nowrap !important;
        border: 0 !important;

        // Fix for positioned table caption that could become anonymous cells
        &:not(caption) {
          position: absolute !important;
        }

        // Prevent copying of this element to clipboard:
        user-select: none;
      }

      /* Scroll bar */
      .forethought-scrollbar {
        /* WebKit Browsers (Chrome, Safari, Edge) */
        ::-webkit-scrollbar {
          height: 5px;
          width: 5px;
        }

        ::-webkit-scrollbar-track {
          background-color: transparent;
          border: none;
          outline: none;
          box-shadow: none;
          -webkit-appearance: none;
        }

        ::-webkit-scrollbar-thumb {
          background-color: ${theme.palette.colors.grey[300]};
          border-radius: 5px;
          border: 2px solid transparent;
        }

        /* Firefox */
        scrollbar-width: thin;
        scrollbar-color: ${theme.palette.colors.grey[300]} transparent;
      }
    `;
  }, [
    theme.palette.colors.grey,
    theme.palette.colors.slate,
    theme.palette.text.primary,
    font,
  ]);

  return <GlobalStyles styles={styles} />;
};

export default GlobalStyle;
